import { unstable_DropdownMenu as DropdownMenu, VisuallyHidden } from '@n26/nxd'
import { useEffect, useRef, useState } from 'react'
import type { DashboardFilters } from '@/features/dashboard/api/schemas'
import { useDashboardFilters } from '@/features/dashboard/filters/useDashboardFilters'
import { useDashboardStructure } from '@/features/dashboard/api/queries'
import { flagUrl } from '@/features/dashboard/filters/flags'
import {
  CheckIcon,
  ChevronsUpDownIcon,
  CloseSmallIcon,
} from '@/shared/ui/icons'
import styles from './FilterBar.module.css'

type PeriodsFilterProps = {
  filters: DashboardFilters
  periods: number
  onChange: (periods: number) => void
}

const PeriodsFilter = ({ filters, periods, onChange }: PeriodsFilterProps) => {
  const selected = filters.periods.options.find(
    option => option.value === periods
  )
  return (
    <div className={styles.group} role='group' aria-labelledby='filter-periods'>
      <span id='filter-periods' className={styles.label}>
        Show data by:
      </span>
      <DropdownMenu>
        <DropdownMenu.Trigger>
          <button type='button' className={`${styles.chip} ${styles.dropdown}`}>
            {selected?.label ?? periods}
            <ChevronsUpDownIcon />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align='start'>
          {filters.periods.options.map(option => (
            <DropdownMenu.Item
              key={option.value}
              onClick={() => onChange(option.value)}
            >
              <span className={styles.option}>
                {option.label}
                {option.value === periods && (
                  <>
                    <CheckIcon />
                    <VisuallyHidden>(selected)</VisuallyHidden>
                  </>
                )}
              </span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  )
}

/**
 * The home page's sub header: the range of months and the markets to show.
 * Both live in the URL (see `useDashboardFilters`). Renders nothing for a
 * dashboard without filters.
 */
export const FilterBar = () => {
  const { data: dashboard } = useDashboardStructure()
  const {
    periods,
    selectedMarkets,
    isMarketFiltered,
    setPeriods,
    toggleMarket,
    clearMarkets,
  } = useDashboardFilters()
  const filters = dashboard?.filters
  const marketsRef = useRef<HTMLDivElement>(null)

  const periodLabel = filters?.periods.options.find(
    option => option.value === periods
  )?.label
  const marketLabel = !isMarketFiltered
    ? 'all markets'
    : (filters?.markets
        .filter(market => selectedMarkets.includes(market.id))
        .map(market => market.label)
        .join(', ') ?? '')
  const summary =
    periodLabel === undefined
      ? null
      : `Showing ${marketLabel}, ${periodLabel.toLowerCase()}`

  // Announced when the selection changes, not when the bar first appears.
  const [announcement, setAnnouncement] = useState('')
  const previousSummary = useRef<string | null>(null)
  useEffect(() => {
    if (summary === null) return
    if (
      previousSummary.current !== null &&
      previousSummary.current !== summary
    ) {
      setAnnouncement(summary)
    }
    previousSummary.current = summary
  }, [summary])

  if (!filters || periods === undefined) return null

  // The Clear button unmounts as it is used, so focus would fall to the body.
  const clear = () => {
    clearMarkets()
    marketsRef.current?.focus()
  }

  return (
    <div className={styles.bar}>
      <PeriodsFilter
        filters={filters}
        periods={periods}
        onChange={setPeriods}
      />
      <VisuallyHidden role='status'>{announcement}</VisuallyHidden>
      <div
        ref={marketsRef}
        tabIndex={-1}
        className={`${styles.group} ${styles.markets}`}
        role='group'
        aria-labelledby='filter-markets'
      >
        <span id='filter-markets' className={styles.label}>
          Filter by market:
          {!isMarketFiltered && (
            <VisuallyHidden> all markets shown</VisuallyHidden>
          )}
        </span>
        <div className={styles.chips}>
          {filters.markets.map(market => {
            const flag = market.flag ? flagUrl(market.flag) : undefined
            // With no market filter there is no pressed state to convey.
            const pressed = isMarketFiltered
              ? selectedMarkets.includes(market.id)
              : undefined
            return (
              <button
                key={market.id}
                type='button'
                className={`${styles.chip} ${flag ? styles.withFlag : styles.plain}`}
                aria-pressed={pressed}
                onClick={() => toggleMarket(market.id)}
              >
                {flag && <img src={flag} alt='' className={styles.flag} />}
                {market.label}
              </button>
            )
          })}
        </div>
        {isMarketFiltered && (
          <button type='button' className={styles.clear} onClick={clear}>
            Clear filters
            <CloseSmallIcon />
          </button>
        )}
      </div>
    </div>
  )
}
